"use client";

import { useEffect, useRef, useState } from "react";
import { Quotes } from "@phosphor-icons/react/dist/ssr";

import { cn } from "@/src/lib/utils";
import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Text } from "@/src/components/ui/Typography";
import { Avatar } from "@/src/components/ui/Avatar";

interface TestimonialProps {
  label: string;
  quote: string;
  authorName: string;
  authorRole: string;
  authorAvatar?: string;
}

// One-shot scroll-triggered reveal, staggered like a small choreographed
// sequence rather than a single flat fade: the divider line draws
// top-to-bottom first, then the quote mark, then the quote text, then the
// attribution — each a beat behind the last. Same IntersectionObserver +
// "animate once" shape Counter already uses elsewhere; skipped entirely
// (renders already "revealed") for prefers-reduced-motion.
export function Testimonial({
  label,
  quote,
  authorName,
  authorRole,
  authorAvatar,
}: TestimonialProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setRevealed(true);
        observer.disconnect();
      },
      { threshold: 0.3 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!quote.trim()) return null;

  return (
    <Section data-dark-section className="relative overflow-hidden bg-neutral-900">
      {/* Ambient background glow — purely decorative, sits behind the
          Container below by DOM order alone (no z-index needed). See
          animations.css for why these are two independently-timed blobs
          rather than one. */}
      <span
        aria-hidden
        className="testimonial-glow-a pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-brand/20 blur-3xl"
      />
      <span
        aria-hidden
        className="testimonial-glow-b pointer-events-none absolute -left-24 -bottom-24 h-96 w-96 rounded-full bg-brand/15 blur-3xl"
      />

      <Container>
        <div className="flex flex-col gap-6 md:flex-row md:gap-16">
          {label && (
            <Text size="titleMD" className="font-medium text-neutral-50 md:w-100 md:shrink-0">
              {label}
            </Text>
          )}

          <div ref={contentRef} className="relative max-w-200 pl-6 sm:pl-8">
            {/* The line lives on its own element (not a `border-l` on the
                content box) so scaling it doesn't also scale the text
                inside — the same reasoning the timeline/node-pulse
                decorative elements in animations.css follow. */}
            <span
              aria-hidden
              className={cn(
                "absolute left-0 top-0 h-full w-px origin-top bg-neutral-400 transition-transform duration-700 ease-out",
                revealed ? "scale-y-100" : "scale-y-0",
              )}
            />

            <Quotes
              size={28}
              weight="fill"
              className={cn(
                "text-neutral-300 transition-all duration-500 delay-150 ease-out",
                revealed ? "scale-100 opacity-100" : "scale-75 opacity-0",
              )}
            />

            <Text
              size="bodyLG"
              className={cn(
                "mt-4 text-neutral-50 italic transition-all duration-500 delay-300 ease-out",
                revealed ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
              )}
            >
              {quote}
            </Text>

            <div
              className={cn(
                "mt-8 flex items-center gap-3 transition-all duration-500 delay-500 ease-out",
                revealed ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
              )}
            >
              <Avatar src={authorAvatar} alt={authorName} name={authorName} size="sm" />

              <div>
                <Text size="bodySMMedium" className="text-neutral-50">
                  {authorName}
                </Text>
                <Text size="bodySM" className="text-neutral-400">
                  {authorRole}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
